import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Instagram, Users, ChevronDown, MapPin } from 'lucide-react';
import { useConnections, useUpdateConnections, useProfile } from '@/lib/api';
import { formatDate, DateFormats } from '@/utils/dateUtils';
import { useToast } from '@/components/ux/Toast';
import { ContactMethodToggles } from './ContactMethodToggles';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/Loader';
import { ProfileImage } from '@/components/ProfileImage';
import { PublicProfileModal } from '@/components/PublicProfileModal';

export function ConnectionsView() {
  const { t } = useT();
  const { data: connectionsData, isLoading } = useConnections();
  const { data: profileData } = useProfile();
  const updateConnections = useUpdateConnections();
  const toast = useToast();
  const connections = useMemo(() => connectionsData?.data || [], [connectionsData]);

  const [sharingByUser, setSharingByUser] = useState<Record<string, string[]>>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // The connection whose public profile is open in the modal (null = closed).
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [savingUsers, setSavingUsers] = useState<Set<string>>(new Set());
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const pendingUpdatesRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const btn = buttonRefs.current.get(openMenuId);
      if (menuRef.current?.contains(target)) return;
      if (btn?.contains(target)) return;
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const userEmail = profileData?.data?.user?.email;
  const userPhone = profileData?.data?.user?.phone;
  const userInstagram = profileData?.data?.user?.instagram;

  const availableMethods = ['email', 'phone', 'instagram'].filter(m => {
    if (m === 'email') return !!userEmail;
    if (m === 'phone') return !!userPhone;
    if (m === 'instagram') return !!userInstagram;
    return false;
  });

  useEffect(() => {
    if (connections.length > 0) {
      const initial: Record<string, string[]> = {};
      connections.forEach(c => {
        if (c.mySharedMethods && c.mySharedMethods.length > 0) {
          initial[c.connectedUser.id] = [...c.mySharedMethods];
        }
      });
      setSharingByUser(initial);
    }
  }, [connections]);

  useEffect(() => {
    const pending = pendingUpdatesRef.current;
    return () => {
      pending.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const saveForUser = useCallback((userId: string, methods: string[]) => {
    const existing = pendingUpdatesRef.current.get(userId);
    if (existing) clearTimeout(existing);

    setSavingUsers(prev => new Set(prev).add(userId));

    const timeout = setTimeout(() => {
      pendingUpdatesRef.current.delete(userId);
      updateConnections.mutate(
        { connections: [{ toUserId: userId, contactMethods: methods as ("email" | "phone" | "instagram")[] }] },
        {
          onSettled: () => {
            setSavingUsers(prev => { const next = new Set(prev); next.delete(userId); return next; });
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.error || t('reservation.failedToUpdateSharing'));
          },
        },
      );
    }, 400);

    pendingUpdatesRef.current.set(userId, timeout);
  }, [updateConnections, toast, t]);

  const handleToggle = useCallback((userId: string, method: string) => {
    setSharingByUser(prev => {
      const current = prev[userId] || [];
      const next = current.includes(method)
        ? current.filter(m => m !== method)
        : [...current, method];
      const updated = { ...prev };
      if (next.length === 0) {
        delete updated[userId];
      } else {
        updated[userId] = next;
      }
      saveForUser(userId, next);
      return updated;
    });
  }, [saveForUser]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card text-center py-16"
      >
        <Users className="w-20 h-20 text-dark-muted mx-auto mb-4" />
        <h3 className="text-2xl font-semibold mb-2">{t('events.noConnectionsYet')}</h3>
        <p className="text-dark-muted max-w-md mx-auto">
          {t('events.noConnectionsDescription')}
        </p>
      </motion.div>
    );
  }

  const getContactIcons = (contactInfo: { email?: string; phone?: string; instagram?: string }) => {
    const icons = [];
    if (contactInfo.email) icons.push({ method: 'email', Icon: Mail, value: contactInfo.email });
    if (contactInfo.phone) icons.push({ method: 'phone', Icon: Phone, value: contactInfo.phone });
    if (contactInfo.instagram) icons.push({ method: 'instagram', Icon: Instagram, value: contactInfo.instagram });
    return icons;
  };

  return (
    <>
    <div className="divide-y divide-neutral-400/15 dark:divide-neutral-500/15">
      {connections.map((connection) => {
        const connectedUser = connection.connectedUser;
        const contactInfo = connection.contactInfo || {};
        const contactIcons = getContactIcons(contactInfo);
        const isMenuOpen = openMenuId === connection.id;
        const myMethods = sharingByUser[connectedUser.id] || [];
        const isSharing = myMethods.length > 0;
        const isSaving = savingUsers.has(connectedUser.id);

        return (
          <motion.div
            key={connection.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-4"
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setProfileUserId(connectedUser.id)}
                aria-label={`View ${connectedUser.firstName}'s profile`}
                className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
              >
                <ProfileImage user={connectedUser} size="lg" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <button
                    type="button"
                    onClick={() => setProfileUserId(connectedUser.id)}
                    className="font-semibold truncate text-left hover:text-primary-600 transition-colors"
                  >
                    {connectedUser.firstName}
                    {connectedUser.lastName ? ` ${connectedUser.lastName}` : ''}
                  </button>
                  {connection.createdAt && (
                    <span className="text-xs text-dark-muted flex-shrink-0">
                      {formatDate(new Date(connection.createdAt), DateFormats.short)}
                    </span>
                  )}
                </div>

                {connectedUser.city && (
                  <p className="flex items-center gap-1 text-sm text-dark-muted mt-0.5 min-w-0">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{connectedUser.city}</span>
                  </p>
                )}

                {contactIcons.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    {contactIcons.map(({ method, Icon, value }) => {
                      const displayValue =
                        method === 'instagram' ? `@${value.replace('@', '')}` : value;
                      const href =
                        method === 'email'
                          ? `mailto:${value}`
                          : method === 'phone'
                            ? `tel:${value}`
                            : `https://instagram.com/${value.replace('@', '')}`;

                      return (
                        <a
                          key={method}
                          href={href}
                          target={method === 'instagram' ? '_blank' : undefined}
                          rel={method === 'instagram' ? 'noopener noreferrer' : undefined}
                          className="flex items-center gap-1.5 text-sm text-dark-muted hover:text-primary-600 transition-colors min-w-0"
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{displayValue}</span>
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-dark-muted mt-1">{t('reservation.noContactInfoShared')}</p>
                )}
              </div>

              {availableMethods.length > 0 && (
                <div className="relative shrink-0" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    ref={(el) => {
                      if (el) buttonRefs.current.set(connection.id, el);
                      else buttonRefs.current.delete(connection.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(isMenuOpen ? null : connection.id);
                    }}
                    className={`btn-inline ${
                      isSharing
                        ? '!bg-primary-700 !text-white hover:!bg-primary-500/25'
                        : ''
                    }`}
                  >
                    {isSharing
                      ? t('reservation.sharingCount', { count: myMethods.length })
                      : t('reservation.shareContactInfo')}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-content bg-white/85 dark:bg-neutral-900/85 backdrop-blur-xl shadow-[0_8px_24px_0_rgba(0,0,0,0.25)] z-10 overflow-hidden">
                      <div className="px-3 py-3">
                        <p className="text-xs text-dark-muted mb-2 font-medium">
                          {t('reservation.youreSharing')}
                        </p>
                        <ContactMethodToggles
                          availableMethods={availableMethods}
                          selectedMethods={myMethods}
                          onToggle={(method) =>
                            handleToggle(connectedUser.id, method)
                          }
                          saving={isSaving}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
    <PublicProfileModal
      userId={profileUserId}
      isOpen={!!profileUserId}
      onClose={() => setProfileUserId(null)}
    />
    </>
  );
}
